import {
    Box,
    Button,
    Card,
    CloseButton,
    Dialog,
    Flex,
    Heading,
    IconButton,
    Portal,
    Spacer,
    Stack,
    Tag,
    Text,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { doc } from 'firebase/firestore'
import { useDocumentData } from '@kage1020/react-firebase-hooks/firestore'
import { QRCodeCanvas } from 'qrcode.react'
import { LuQrCode, LuCopy, LuDownload, LuVideo, LuFilm } from 'react-icons/lu'

import { db } from '../firebase'
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/AuthContext'
import { useEventTypes } from '../contexts/EventTypesContext'
import { displayWebappUrl } from '../utils/webappUrl'
import { fetchEventContentCounts } from '../services/firebaseService'
import { toaster } from './ui/toaster'

export default function EventListItem({ eventId, eventName }) {
    const navigate = useNavigate()
    const { t, lang } = useTranslation()
    const { isModeratorOnly } = useAuth()
    const [qrOpen, setQrOpen] = useState(false)
    const qrRef = useRef(null)

    const [eventData] = useDocumentData(doc(db, 'events', eventId))
    const [counts, setCounts] = useState(null)

    useEffect(() => {
        let cancelled = false
        fetchEventContentCounts(eventId).then((c) => { if (!cancelled) setCounts(c) })
        return () => { cancelled = true }
    }, [eventId])

    const countLabel = (n, one, many) => t(n === 1 ? one : many, { count: n })

    const { getTypeConfig } = useEventTypes()
    const typeConfig = eventData?.eventType ? getTypeConfig(eventData.eventType) : null
    const name = eventName ?? eventData?.name ?? t('events.untitled')

    // In production, prefer the URL stored on the event (single source of truth,
    // also read by the desktop app); in dev, point at the local webapp. Both use
    // the /{slug}/{eventId} scheme.
    const qrUrl = displayWebappUrl({
        storedUrl: eventData?.webappUrl,
        typeConfig,
        eventType: eventData?.eventType,
        eventId,
    })

    const handleRowClick = () => {
        // Moderators only ever work in moderation, so skip the settings view.
        navigate(isModeratorOnly(eventId) ? `/events/${eventId}/moderation` : `/events/${eventId}`)
    }

    const openQr = (e) => {
        e.stopPropagation()
        setQrOpen(true)
    }

    const copyUrl = (e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(qrUrl)
        toaster.create({ title: t('common.urlCopied'), status: 'success' })
    }

    const downloadQr = () => {
        const canvas = qrRef.current?.querySelector('canvas')
        if (!canvas) return
        const link = document.createElement('a')
        link.download = `event-${eventId}-qr.png`
        link.href = canvas.toDataURL()
        link.click()
    }

    return (
        <>
            <Card.Root
                bg="var(--mc-bg-surface)"
                border="1px solid"
                borderColor="var(--mc-border)"
                                cursor="pointer"
                _hover={{ borderColor: 'var(--mc-border-strong)', bg: 'var(--mc-bg-raised)' }}
                transition="all 0.15s ease"
                onClick={handleRowClick}
            >
                <Card.Body py={3} px={4}>
                    <Flex align="center" gap={3}>
                        <Heading size="md" color="var(--mc-text)" truncate>
                            {name}
                        </Heading>
                        <Tag.Root fontFamily="'JetBrains Mono', monospace" fontSize="xs" display={{ base: 'none', md: 'inline-flex' }}>
                            {eventId}
                        </Tag.Root>
                        {typeConfig && (
                            <Tag.Root colorPalette={typeConfig.tagColor}>{typeConfig.label?.[lang] || typeConfig.label?.en || eventData?.eventType}</Tag.Root>
                        )}
                        <Spacer />
                        {counts?.videos != null && (
                            <Tag.Root variant="outline" display={{ base: 'none', sm: 'inline-flex' }}>
                                <Tag.StartElement><LuVideo /></Tag.StartElement>
                                <Tag.Label>{countLabel(counts.videos, 'events.videoCountOne', 'events.videoCountMany')}</Tag.Label>
                            </Tag.Root>
                        )}
                        {counts?.reels != null && (
                            <Tag.Root variant="outline" display={{ base: 'none', sm: 'inline-flex' }}>
                                <Tag.StartElement><LuFilm /></Tag.StartElement>
                                <Tag.Label>{countLabel(counts.reels, 'events.reelCountOne', 'events.reelCountMany')}</Tag.Label>
                            </Tag.Root>
                        )}
                        <IconButton
                            aria-label={t('events.showQrCode')}
                            size="sm"
                            variant="ghost"
                            onClick={openQr}
                        >
                            <LuQrCode />
                        </IconButton>
                    </Flex>
                </Card.Body>
            </Card.Root>

            <Dialog.Root open={qrOpen} onOpenChange={(e) => setQrOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content
                            onClick={(e) => e.stopPropagation()}
                            mx={{ base: 3, md: 6 }}
                            maxW={{ base: 'calc(100vw - 24px)', md: 'md' }}
                            maxH={{ base: 'calc(100vh - 24px)', md: '90vh' }}
                            overflowY="auto"
                        >
                            <Dialog.Header>
                                <Dialog.Title pr={8} wordBreak="break-word">
                                    {name} — {t('qr.title')}
                                </Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Stack gap={3} align="stretch">
                                    <Flex justify="center">
                                        <Box
                                            bg="white"
                                            p={{ base: 3, md: 4 }}
                                            borderRadius="lg"
                                            ref={qrRef}
                                        >
                                            <QRCodeCanvas
                                                value={qrUrl}
                                                size={240}
                                                style={{ width: '100%', height: 'auto', maxWidth: 280 }}
                                            />
                                        </Box>
                                    </Flex>
                                    <Text
                                        fontSize="xs"
                                        color="fg.muted"
                                        wordBreak="break-all"
                                        textAlign="center"
                                        userSelect="all"
                                    >
                                        {qrUrl}
                                    </Text>
                                    <Stack gap={2} align="stretch">
                                        <Button variant="outline" onClick={copyUrl}>
                                            <LuCopy /> {t('qr.copyEventUrl')}
                                        </Button>
                                        <Button variant="outline" onClick={downloadQr}>
                                            <LuDownload /> {t('qr.downloadQr')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Dialog.Body>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </>
    )
}
